import React, { FC } from "react";
import { useHistory, useParams } from "react-router-dom";
import { EditOutlined, EyeOutlined } from "@ant-design/icons";
import { Button, Typography } from "antd";
import {
  CONSEQUENCE_CLASSIFICATION_CODE_HASH,
  DAM_OPERATING_STATUS_HASH,
  EMPTY_FIELD,
} from "@mds/common/constants/strings";
import { getHighestConsequence } from "@common/utils/helpers";
import {
  getITRBExemptionStatusCodeOptionsHash,
  getTSFOperatingStatusCodeOptionsHash,
} from "@mds/common/redux/selectors/staticContentSelectors";
import { storeDam } from "@mds/common/redux/slices/damSlice";
import CoreTable from "@mds/common/components/common/CoreTable";
import { EDIT_OUTLINE_VIOLET } from "@/constants/assets";
import AuthorizationWrapper from "@/components/common/wrappers/AuthorizationWrapper";
import * as Permission from "@/constants/permissions";
import { EDIT_DAM, MINE_TAILINGS_DETAILS } from "@/constants/routes";
import { IDam, ITailingsStorageFacility } from "@mds/common/interfaces";
import { ColumnsType } from "antd/lib/table";
import {
  renderCategoryColumn,
  renderTextColumn,
  renderActionsColumn,
} from "@mds/common/components/common/CoreTableCommonColumns";
import { useAppDispatch, useAppSelector } from "@mds/common/redux/rootState";

interface MineTailingsTableProps {
  tailings: ITailingsStorageFacility[];
  isLoaded: boolean;
  openEditTailingsModal: (
    tailings,
    handleEditTailings: (event, tailings: ITailingsStorageFacility) => void,
    record
  ) => void;
  handleEditTailings: (event, tailings: ITailingsStorageFacility) => void;
  tsfV2Enabled: boolean;
  canEditTSF: boolean;
}

const MineTailingsTable: FC<MineTailingsTableProps> = (props) => {
  const { mineGuid } = useParams<{ mineGuid: string }>();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const TSFOperatingStatusCodeHash = useAppSelector(getTSFOperatingStatusCodeOptionsHash);
  const itrmExemptionStatusCodeHash = useAppSelector(getITRBExemptionStatusCodeOptionsHash);
  const {
    openEditTailingsModal,
    handleEditTailings,
    tailings,
    tsfV2Enabled,
    canEditTSF,
  } = props;

  const transformRowData = (items: ITailingsStorageFacility[]) => {
    return items?.map((tailing) => {
      return {
        key: tailing.mine_tailings_storage_facility_guid,
        ...tailing,
      };
    });
  };

  const handleEditDam = (event, dam: IDam, isEditMode, canEditDam) => {
    event.preventDefault();
    dispatch(storeDam(dam));

    const url = EDIT_DAM.dynamicRoute(
      mineGuid,
      dam.mine_tailings_storage_facility_guid,
      dam.dam_guid,
      isEditMode,
      canEditDam
    );
    history.push(url);
  };

  const renderOldTSFActions = () => {
    return {
      key: "actions",
      fixed: "right",
      render: (record) => {
        return (
          <div>
            <AuthorizationWrapper permission={Permission.EDIT_TSF}>
              <Button
                type="primary"
                size="small"
                ghost
                onClick={(event) => openEditTailingsModal(event, handleEditTailings, record)}
              >
                <img src={EDIT_OUTLINE_VIOLET} alt="Edit TSF" />
              </Button>
            </AuthorizationWrapper>
          </div>
        );
      },
    };
  };

  const newTSFActions = [
    {
      key: "view",
      label: "View TSF",
      icon: <EyeOutlined />,
      clickFunction: (_event, record) => {
        history.push({
          pathname: MINE_TAILINGS_DETAILS.dynamicRoute(
            record.mine_tailings_storage_facility_guid,
            record.mine_guid,
            "basic-information",
            false
          ),
        });
      },
    },
    ...(canEditTSF
      ? [
        {
          key: "edit",
          label: "Edit TSF",
          icon: <EditOutlined />,
          clickFunction: (_event, record) => {
            history.push({
              pathname: MINE_TAILINGS_DETAILS.dynamicRoute(
                record.mine_tailings_storage_facility_guid,
                record.mine_guid,
                "basic-information",
                true
              ),
            });
          },
        },
      ]
      : []),
  ];

  const damActions = [
    {
      key: "view",
      label: "View Dam",
      icon: <EyeOutlined />,
      clickFunction: (event, record) => {
        handleEditDam(event, record, false, false);
      },
    },
    ...(canEditTSF
      ? [
        {
          key: "edit",
          label: "Edit Dam",
          icon: <EditOutlined />,
          clickFunction: (event, record) => {
            handleEditDam(event, record, true, true);
          },
        },
      ]
      : []),
  ];

  const columns = [
    {
      title: "Name",
      dataIndex: "mine_tailings_storage_facility_name",
      render: (text) => <div title="Name">{text}</div>,
    },
    {
      title: "Operating Status",
      dataIndex: "tsf_operating_status_code",
      render: (text) => (
        <div title="Operating Status">{TSFOperatingStatusCodeHash[text] ?? EMPTY_FIELD}</div>
      ),
    },
    {
      title: "Submission Status",
      dataIndex: "is_draft",
      render: (is_draft: boolean) => <div title="Submission Status">{is_draft ? "Draft" : "Submitted"}</div>
    },
    {
      title: "Consequence Classification",
      key: "consequence_classification_status_code",
      render: (record) => <Typography.Text>{getHighestConsequence(record)}</Typography.Text>,
    },
    {
      title: "Independent Tailings Review Board",
      dataIndex: "itrb_exemption_status_code",
      width: "5%",
      render: (text) => (
        <div title="Has Independent Tailings Review Board?">
          {itrmExemptionStatusCodeHash[text] ?? EMPTY_FIELD}
        </div>
      ),
    },
    {
      title: "Permit #",
      dataIndex: "mines_act_permit_no",
      key: "mines_act_permit_no",
      sorter: (a, b) => (a.itrb_exemption_status_code > b.itrb_exemption_status_code ? -1 : 1),
    },
    {
      title: "Engineer of Record",
      dataIndex: "engineer_of_record",
      render: (text) => (
        <div title="Engineer of Record">{text ? text.party.name : EMPTY_FIELD}</div>
      ),
    },
    {
      title: "Qualified Person",
      dataIndex: "qualified_person",
      render: (text) => <div title="Qualified Person">{text ? text.party.name : EMPTY_FIELD}</div>,
    },
    {
      title: "Latitude",
      dataIndex: "latitude",
      render: (text) => <div title="Latitude">{text ?? EMPTY_FIELD}</div>,
    },
    {
      title: "Longitude",
      dataIndex: "longitude",
      render: (text) => <div title="Longitude">{text ?? EMPTY_FIELD}</div>,
    },
    ...(tsfV2Enabled ? [renderActionsColumn({ actions: newTSFActions })] : [renderOldTSFActions()]),
  ];

  const damColumns = [
    renderTextColumn("dam_name", "Dam Name"),
    renderCategoryColumn("operating_status", "Operating Status", DAM_OPERATING_STATUS_HASH),
    renderCategoryColumn(
      "consequence_classification",
      "Consequence Classification",
      CONSEQUENCE_CLASSIFICATION_CODE_HASH
    ),
    renderActionsColumn({ actions: damActions }),
  ];

  return (
    <CoreTable
      condition={props.isLoaded}
      dataSource={transformRowData(tailings)}
      columns={columns as ColumnsType<ITailingsStorageFacility>}
      rowKey="mine_tailings_storage_facility_guid"
      classPrefix="tailings"
      expandProps={
        !tsfV2Enabled
          ? null
          : {
            rowKey: "dam_guid",
            rowExpandable: (record: any) => record.dams?.length > 0,
            recordDescription: "associated dams",
            getDataSource: (record: any) => record.dams,
            subTableColumns: damColumns,
          }
      }
    />
  );
};

export default MineTailingsTable;